import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { Small } from "@/components/Typography";
import { formatDate } from "@/utils/helpers";
import stringToTitleCase from "@/utils/stringToTitleCase";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { tv } from "tailwind-variants";

interface IProps {
  activeContactDetails?: IGetContactDetailsRS;
}

const styles = tv({
  slots: {
    header: "flex flex-row border-t border-t-gray px-30 py-10 font-bold",
    section:
      "flex w-full flex-grow flex-row border-t border-t-gray py-5 *:[&:last-child]:border-b *:[&:last-child]:border-b-gray",
    sectionHeader:
      "flex basis-2/3 flex-row px-30 py-10 font-semibold text-gray-dark",
    sectionValue: "flex basis-1/3 items-center",
  },
});

const ConsentRecordDetail = ({ activeContactDetails }: IProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<string>("");
  const { t } = useTranslation();

  const { header, section, sectionHeader, sectionValue } = styles();

  const toggleModal = () => {
    setIsModalOpen(isModalOpen => !isModalOpen);
  };

  const toggleModalAndSetContent = (modalContent: string) => () => {
    setModalContent(modalContent);
    toggleModal();
  };

  const renderBadge = (isMarketable: boolean) => (
    <Badge variant={isMarketable ? "success" : "error"}>
      {!!isMarketable
        ? t("common.contactDetailsView.marketable")
        : t("common.contactDetailsView.nonMarketable")}
    </Badge>
  );

  const renderLink = (url: string, value?: string | number) => (
    <Button as="a" variant="link" target="_blank" href={url} width="unset">
      {value}
    </Button>
  );

  const renderModalButton = (modal: string, value?: string | number) => (
    <Button
      as="button"
      variant="link"
      onClick={toggleModalAndSetContent(modal)}
      width="unset">
      {value}
    </Button>
  );

  const renderModal = () => (
    <Modal isOpen={isModalOpen} toggleIsOpen={toggleModal}>
      <div dangerouslySetInnerHTML={{ __html: modalContent }} />
    </Modal>
  );

  const renderFormattedFormType = (value: IContactDetailFormType) =>
    t(`common.contactDetailsView.table.formType.${value}`);

  const renderFormattedRationale = (value: IContactRationale) =>
    t(`common.contacts.rationale.${value}`);

  const renderFormattedAction = (value: IContactDetailAction) =>
    t(
      `common.contactDetailsView.table.action.${value?.toLowerCase() as IContactDetailAction}`,
    );

  const renderTimeStamp = (value: string) => formatDate(value);

  const renderLegalBasis = (value: IContactDetailLegalBasis) =>
    t(`common.contactDetailsView.table.legalBasis.${value}`);

  const renderSectionValue = ({
    value,
    url,
    modal,
    property,
  }: {
    value:
      | string
      | number
      | IContactDetailFormType
      | IContactRationale
      | IContactDetailAction
      | IContactDetailLegalBasis;
    url?: string;
    modal?: string;
    property?:
      | "NAME"
      | "STATUS"
      | "FORM_TYPE"
      | "ACTION"
      | "TIMESTAMP"
      | "LEGAL_BASIS";
  }) => {
    if (value === "Non-Marketable" || value === "Marketable")
      return renderBadge(value === "Marketable");

    if (!!url) return renderLink(url, value);

    if (!!modal) return renderModalButton(modal, value);

    if (property === "FORM_TYPE" && !!value)
      return renderFormattedFormType(value as IContactDetailFormType);

    if (property === "STATUS" && !!value)
      return renderFormattedRationale(value as IContactRationale);

    if (property === "ACTION" && !!value)
      return renderFormattedAction(value as IContactDetailAction);

    if (property === "TIMESTAMP" && typeof value === "string")
      return renderTimeStamp(value);

    if (property === "LEGAL_BASIS" && !!value)
      return renderLegalBasis(value as IContactDetailLegalBasis);

    if (property === "NAME")
      return !!value ? value : t("common.contactDetailsView.table.nameUnknown");

    return value;
  };

  return (
    <>
      {activeContactDetails?.sections?.map(({ heading, properties }, i) => (
        <Fragment key={i}>
          <Small testId="contact-details-section-header" className={header()}>
            {heading.replaceAll("_", " ")}
          </Small>
          {properties.map(({ label, value, url, modal, property }, i) => (
            <div key={i} className={section()}>
              <Small
                testId="contact-details-section-label"
                className={sectionHeader()}>
                {stringToTitleCase(label.replaceAll("_", " "))}
              </Small>
              <Small
                testId="contact-details-section-value"
                className={sectionValue()}>
                {renderSectionValue({ value, url, modal, property })}
              </Small>
            </div>
          ))}
        </Fragment>
      ))}
      {renderModal()}
    </>
  );
};

export default ConsentRecordDetail;
